import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { AfterViewInit, Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, from, Observable, switchMap } from 'rxjs';
import { GridService } from '../../grid.service';
import { LookupCellEditorParams } from '../../grid.types';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { PopupService } from '@app/core/services/popup.service';
import { DataTaskService } from '@app/core/services/data-task.service';
import { MatChipInputEvent } from '@angular/material/chips';
import type { GridRefreshableCellEditor } from '../../helpers/grid-active-editor.helpers';

@Component({
    selector: 'tt-lookup-multi-cell-editor',
    templateUrl: './lookup-multi-cell-editor.component.html',
    styleUrls: ['./lookup-multi-cell-editor.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class LookupMultiCellEditorComponent implements ICellEditorAngularComp, AfterViewInit, GridRefreshableCellEditor {
    /**
     * List of selected ids displayed as chips.
     */
    public selectedIds?: string[];

    public chipsList?: { [key: string]: any }[];

    public dataId = 'item_id';

    public dataName = 'item_name';

    public dataState = 'item_state';

    public dataParms = 'item_parms';

    public dataColor = 'item_color';

    public dataTextColor = 'item_text_color';

    /**
     * Cell editor params passed from ag-grid.
     */
    public params?: LookupCellEditorParams;

    /**
     * Form-control for the search field.
     */
    public searchControl = new FormControl('');

    /**
     * Whether the value in the cell has been edited since editing started.
     */
    public hasBeenEdited: boolean = false;

    /**
     * List of options to display from the search.
     */
    public filteredOptions?: Observable<{ [key: string]: string }[]>;

    public model = '';

    /**
     * The options displayed in the search.
     */
    public data?: Observable<{ [key: string]: string }[]>;

    public id = {
        wrapper: crypto.randomUUID(),
        input: crypto.randomUUID(),
    };

    /**
     * Element reference to the input element.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    /**
     * Reference to the trigger for the listbox displaying search options.
     */
    @ViewChild(MatAutocompleteTrigger)
    public autoComplete?: MatAutocompleteTrigger;

    constructor(
        private elementRef: ElementRef,
        private gridService: GridService,
        private popup: PopupService,
        private datatask: DataTaskService,
    ) {}

    private markAsEdited(): void {
        this.hasBeenEdited = true;
        this.params?.onCellEditorValueChanged?.();
    }

    public trackItem = (_: number, item: { [key: string]: string }) => {
        return item[this.params!.key];
    };
    /**
     * Adds a chip from selection in autocomplete.
     *
     * @param event the autocomplete selection event.
     */
    public selected(event: MatAutocompleteSelectedEvent): void {
        this.markAsEdited();

        if (!!this.selectedIds) {
            const key = this.params?.relations.find((relation) => relation.key === this.params?.colDef.colId)?.value ?? 'item_id';
            this.selectedIds?.push(event.option.value[key]);
        } else if (!!this.chipsList) {
            let newData: any = {};

            for (let { key, value } of this.params?.relations ?? []) {
                newData[key] = event.option.value[value];
            }

            this.chipsList.push(newData);
        }

        this.setSelectedItemsToModel();

        if (!!this.inputRef) this.inputRef.nativeElement.value = '';

        this.searchControl.setValue(null);
    }

    public add(event: MatChipInputEvent): void {
        this.markAsEdited();

        const value = (event.value || '').trim();

        this.selectedIds?.push(value);

        this.setSelectedItemsToModel();

        event.chipInput!.clear();
    }

    public remove(id: string | any): void {
        this.markAsEdited();

        let index = -1;

        if (typeof id === 'string' && !!this.selectedIds) {
            index = this.selectedIds.indexOf(id);

            if (index >= 0) {
                this.selectedIds.splice(index, 1);
            }
        } else if (typeof id === 'object' && !!this.chipsList) {
            index = this.chipsList.indexOf(id);

            if (index >= 0) {
                this.chipsList.splice(index, 1);
            }
        }

        this.setSelectedItemsToModel();
    }

    private setSelectedItemsToModel() {
        if (!!this.selectedIds) {
            this.selectedIds = this.selectedIds.filter((id) => !!id);
            this.model = this.selectedIds.reduce((acc, id) => `${acc}${id},`, '');
        } else if (!!this.chipsList) {
            this.chipsList = this.chipsList.filter((chip) => !!chip);
            this.chipsList = this.chipsList.map((chip) => {
                const data: { [key: string]: any } = {};

                for (let { key, value } of this.params?.relations ?? []) {
                    data[key] = chip[key];
                }

                return data;
            });
        }
    }

    public agInit(params: LookupCellEditorParams): void {
        this.params = params;

        if (typeof this.params.value === 'string') {
            this.model = this.params.value;
            this.selectedIds = this.params.value.split(',').filter((id: string) => !!id);
        } else if (Array.isArray(this.params.value)) {
            this.chipsList = this.params.value;
        }

        this.data = this.searchControl.valueChanges.pipe(
            debounceTime(250),
            switchMap((value) => from(this.getData(value))),
        );
    }

    public setValue(value: string | any[], markAsEdited: boolean = true) {
        if (markAsEdited === true) {
            this.markAsEdited();
        }

        if (typeof value === 'string') {
            this.selectedIds = value.split(',').filter((id: string) => !!id);
        } else if (Array.isArray(value)) {
            this.chipsList = value;
        }
    }

    public syncValueFromRow(value: unknown): void {
        this.setValue(value as string | any[], false);
        setTimeout(() => {
            this.inputRef?.nativeElement.select();
        }, 1);
    }

    public onBlur(_: MatAutocomplete, event: FocusEvent) {
        if (event.relatedTarget === null) {
            event.stopImmediatePropagation();
            event.stopPropagation();
        }

        if (!!this.inputRef) this.inputRef.nativeElement.value = '';

        this.searchControl.setValue(null);
    }

    private getData(search: string | null) {
        if (!this.params || search === null) return [];
        return this.gridService.lookupSearch(Number(this.params.method), this.params.field, search, this.params?.node.data);
    }

    public getValue() {
        if (!!this.chipsList) {
            return this.chipsList;
        }

        return this.model;
    }

    ngAfterViewInit() {
        window.addEventListener('focusout', (event) => event.stopPropagation());
        window.setTimeout(() => {
            if (!!this.params && this.inputRef && (this.params.eventKey ?? '').length === 1) {
                this.inputRef.nativeElement.value = this.params.eventKey;
                this.inputRef?.nativeElement.focus();
            } else {
                this.inputRef?.nativeElement.focus();
                this.inputRef?.nativeElement.select();
            }

            const panelEl = this.autoComplete?.autocomplete?.panel?.nativeElement as HTMLElement | undefined;

            if (!panelEl) return;

            panelEl.classList.add('ag-custom-component-popup');

            const parentPanel = panelEl.parentElement;

            if (!!parentPanel) parentPanel.classList.add('ag-custom-component-popup');
        });
    }
}
